import axios, { AxiosResponse } from 'axios';

export const GCS_CHUNK_SIZE = 8 * 1024 * 1024;
const MAX_CHUNK_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 1000;
const RESUME_INCOMPLETE = 308;

// A separate instance: the global axios carries the OOVA baseURL and the
// Firebase Authorization header, and the upload URL itself is the credential.
export const gcsClient = axios.create({
  validateStatus: (status) =>
    (status >= 200 && status < 300) || status === RESUME_INCOMPLETE,
});

export interface ResumableUploadOptions {
  chunkSize?: number;
  contentType?: string;
  onProgress?: (uploadedBytes: number) => void;
  signal?: AbortSignal;
  resume?: boolean;
  retryDelayMs?: number;
}

type ChunkResult = { done: true } | { done: false; nextOffset: number };

const toChunkResult = (response: AxiosResponse): ChunkResult => {
  if (response.status !== RESUME_INCOMPLETE) {
    return { done: true };
  }

  const range = response.headers['range'] as string | undefined;
  const lastByte = range?.match(/bytes=0-(\d+)/)?.[1];

  return {
    done: false,
    nextOffset: lastByte === undefined ? 0 : Number(lastByte) + 1,
  };
};

const isRetryable = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;

  return status === undefined || status >= 500 || status === 429;
};

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });

export const queryUploadStatus = async (
  uploadUrl: string,
  totalBytes: number,
  signal?: AbortSignal
): Promise<ChunkResult> =>
  toChunkResult(
    await gcsClient.put(uploadUrl, null, {
      headers: { 'Content-Range': `bytes */${totalBytes}` },
      signal,
    })
  );

export const uploadResumable = async (
  uploadUrl: string,
  file: Blob,
  {
    chunkSize = GCS_CHUNK_SIZE,
    contentType,
    onProgress,
    signal,
    resume = false,
    retryDelayMs = RETRY_BASE_DELAY_MS,
  }: ResumableUploadOptions = {}
): Promise<void> => {
  const total = file.size;
  let offset = 0;
  let failures = 0;
  let mustQueryStatus = resume;

  for (;;) {
    try {
      if (mustQueryStatus) {
        const status = await queryUploadStatus(uploadUrl, total, signal);
        mustQueryStatus = false;

        if (status.done) {
          onProgress?.(total);

          return;
        }

        offset = status.nextOffset;
        onProgress?.(offset);
      }

      const end = Math.min(offset + chunkSize, total);
      const chunkStart = offset;
      const response = await gcsClient.put(
        uploadUrl,
        file.slice(chunkStart, end),
        {
          headers: {
            'Content-Range': `bytes ${chunkStart}-${end - 1}/${total}`,
            ...(contentType ? { 'Content-Type': contentType } : {}),
          },
          signal,
          onUploadProgress: (event) =>
            onProgress?.(chunkStart + (event.loaded ?? 0)),
        }
      );
      const result = toChunkResult(response);

      if (result.done) {
        onProgress?.(total);

        return;
      }

      offset = result.nextOffset;
      failures = 0;
      onProgress?.(offset);
    } catch (error) {
      if (signal?.aborted || !isRetryable(error)) {
        throw error;
      }

      failures += 1;

      if (failures > MAX_CHUNK_RETRIES) {
        throw error;
      }

      await wait(retryDelayMs * 2 ** (failures - 1), signal);
      mustQueryStatus = true;
    }
  }
};
